import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DocumentService } from '../../../shared/services/document/document.service';
import { ToasterService } from 'src/app/modules/shared/services/toaster/toaster.service';
import { OrganizationGroupService } from 'src/app/modules/shared/services/organization-group/organization-group.service';


export interface FileRecord {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error'; // Added status property
  errorMessage?: string; // Optional error message property
  isMultipart?: boolean;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss']
})
export class FileUploadComponent implements OnInit, OnDestroy {

  public empty_page_greeting: string = 'Your files will appear here...';
  public sending: boolean = false;
  public files: FileRecord[] = [];
  public errors: string[] = [];
  public selectedHouseholdId: number | null = null;

  private firmContextSubscription: Subscription | null = null;

  constructor(
    private DocumentService: DocumentService,
    private Toaster: ToasterService,
    private organizationGroupService: OrganizationGroupService,
  ) { }

  private checkForErrors() {
    this.errors = [];
    // Currently there are no errors to check for. 
    // Any file that is added will be accepted
    // Keeping this around as a convenient place to add
    // validation in the future if needed. Just push to the
    // errors array if you find an error.
  }

  public sendButtonEnabled() {
    return this.files.length > 0 && this.errors.length === 0;
  }

  private newFileRecord(file: File): FileRecord {
    return {
      file,
      status: 'pending' // Initialize status as pending
    };
  }

  public handleFileChange(files: File[]) {
    this.files.push(...files.map(file => this.newFileRecord(file)));
    this.checkForErrors();
  }

  public removeFile(index: number): void {
    this.files.splice(index, 1);
    this.checkForErrors();
    this.Toaster.success('File removed');
  }

  public resetFiles(): void {
    this.files = [];
  }

  public async sendFiles(): Promise<void> {
    try {
      this.sending = true;

      // Upload files one by one
      for (const fileRecord of this.files) {

        if (fileRecord.status === 'success') continue; // Skip already uploaded files



        fileRecord.status = 'uploading'; // Set status to uploading
        try {
          // TESTING ONLY, SIMULATE FAILURE FOR FILE WITH NAME 'error.txt'
          if (fileRecord.file.name === 'error.txt') {
            throw new Error(`Cannot upload a file named "error.txt"`);
          }

          await this.DocumentService.uploadFile(fileRecord.file, {
            isMultipart: fileRecord.isMultipart || false,
            household_id: this.selectedHouseholdId !== null ? String(this.selectedHouseholdId) : undefined,
          });
          fileRecord.status = 'success'; // Set status to success
        } catch (error: any) {
          console.error(`Error uploading file '${fileRecord.file.name}':`, error);
          fileRecord.status = 'error'; // Set status to error
          fileRecord.errorMessage = error?.message ? error.message : 'An error occurred during upload. Retry or contact support.'; // Set error message
        }
      }

      // Reset files only if all uploads are successful
      this.resetIfSuccessful()

    } catch (error) {
      this.Toaster.warning('An error occurred while uploading files.');
      console.error('Error during file upload:', error);

    } finally {
      this.sending = false;
    }
  }

  public async retryFileUpload(index: number): Promise<void> {

    const fileRecord = this.files[index];
    fileRecord.status = 'uploading'; // Set status to uploading
    fileRecord.errorMessage = ''; // Clear previous error message

    try {
      await this.DocumentService.uploadFile(fileRecord.file, {
        isMultipart: fileRecord.isMultipart || false,
        household_id: this.selectedHouseholdId !== null ? String(this.selectedHouseholdId) : undefined,
      });
      fileRecord.status = 'success'; // Set status to success

    } catch (error: any) {
      console.error(`Error retrying file '${fileRecord.file.name}':`, error);
      fileRecord.status = 'error'; // Set status to error
      fileRecord.errorMessage = error?.message ? error.message : 'An error occurred during upload. Retry or contact support.'; // Set error message
    }

    this.resetIfSuccessful(); // Check if all files are successfully uploaded
  }

  private resetIfSuccessful(): void {
    if (this.files.every(file => file.status === 'success')) {
      this.Toaster.success('All files uploaded successfully 🥳');
      this.empty_page_greeting = 'Your files have been uploaded successfully!<br><br>Stay here to add more files, or go to the Folders page to start your review.';
      this.resetFiles();
    }
  }

  public setMultipartFlag(index: number, isMultipart: boolean): void {
    this.files[index].isMultipart = isMultipart;
    console.log(this.files[index]);
  }

  ngOnInit(): void {
    // Selected household belongs to the previous firm — clear it on switch.
    // Pending file picks stay (the user can still upload them to the new
    // firm by re-selecting a household).
    this.firmContextSubscription = this.organizationGroupService.firmContextChanged$
      .subscribe(() => {
        this.selectedHouseholdId = null;
        this.errors = [];
      });
  }

  ngOnDestroy(): void {
    if (this.firmContextSubscription) {
      this.firmContextSubscription.unsubscribe();
    }
  }
}
